/**
 * "Cancelar caso" no cabeçalho da mesa (E224.F2, owner 05/11, ADR-0151 §5).
 *
 * Botão vermelho ao lado do "Mais": abre o diálogo que pede o motivo (de 10 a 500 caracteres) e
 * diz o que acontece — o caso para, e o cancelamento não se desfaz. Só a equipe chega à mesa; o
 * servidor confere de novo e é quem decide: a tela mostra a recusa dele dentro do diálogo.
 */
import type { AxiosInstance } from 'axios';
import { useId, useState } from 'react';

import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import type { CaseRecordData } from '@/lib/cases';
import {
  CancelamentoError,
  cancelarCaso,
  MOTIVO_MAXIMO_DO_CANCELAMENTO,
  motivoValido,
} from '@/lib/cancelamento_do_caso';

const t = ptBR.mesaUnica.cancelamento;

/**
 * A mesa só para consulta: o exame assinado (o documento diz; sem documento lido, vale o assinado,
 * como antes) ou o caso cancelado (E224.F2) — o dado não abre para edição.
 */
export function soParaConsulta(
  documento: { assinado?: boolean } | null | undefined,
  caso: CaseRecordData | null,
): boolean {
  return (documento?.assinado ?? true) || (caso?.cancelamento ?? null) !== null;
}

export interface CancelarCasoProps {
  client: AxiosInstance;
  caseId: string;
  /** O caso da mesa; sem ele lido ainda, o botão não aparece. O código vai no título do diálogo. */
  caso: CaseRecordData | null;
  /** O cancelamento gravou: a mesa relê o caso. */
  onCancelado: () => void;
}

export function CancelarCaso({ client, caseId, caso, onCancelado }: CancelarCasoProps) {
  const campo = useId();
  const ajuda = useId();
  const [aberto, setAberto] = useState(false);
  const [motivo, setMotivo] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  function mudarAberto(proximo: boolean): void {
    if (enviando) return;
    setAberto(proximo);
    if (!proximo) {
      setMotivo('');
      setErro(null);
    }
  }

  async function cancelar(): Promise<void> {
    setEnviando(true);
    setErro(null);
    try {
      await cancelarCaso(client, caseId, motivo);
      setAberto(false);
      setMotivo('');
      onCancelado();
    } catch (err) {
      setErro(t.recusa[err instanceof CancelamentoError ? err.motivo : 'indisponivel']);
    } finally {
      setEnviando(false);
    }
  }

  if (caso === null) return null;
  return (
    <>
      <Button
        type="button"
        variant="danger"
        onClick={() => mudarAberto(true)}
        data-testid="mesa-acao-cancelar-caso"
      >
        {t.acao}
      </Button>
      <Dialog open={aberto} onOpenChange={mudarAberto}>
        <DialogContent data-testid="cancelar-caso-dialogo">
          <DialogHeader>
            <DialogTitle>{t.titulo.replace('{codigo}', caso.display_code)}</DialogTitle>
            <DialogDescription>{t.explicacao}</DialogDescription>
          </DialogHeader>
          <div className="mt-sc-4 flex flex-col gap-sc-2">
            <label htmlFor={campo} className="text-body-sm text-fg">
              {t.motivo}
            </label>
            <textarea
              id={campo}
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              rows={3}
              maxLength={MOTIVO_MAXIMO_DO_CANCELAMENTO}
              aria-describedby={ajuda}
              className="border border-border bg-surface p-sc-2 text-body-sm text-fg"
              data-testid="motivo-do-cancelamento"
            />
            <p id={ajuda} className="text-caption text-fg-muted">
              {t.ajuda}
            </p>
            {erro !== null ? (
              <p
                role="alert"
                className="border border-danger bg-danger-50 px-3 py-2 text-body-sm text-danger-700"
                data-testid="cancelar-caso-erro"
              >
                {erro}
              </p>
            ) : null}
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="secondary"
              onClick={() => mudarAberto(false)}
              disabled={enviando}
            >
              {t.voltar}
            </Button>
            <Button
              type="button"
              variant="danger"
              onClick={() => void cancelar()}
              disabled={!motivoValido(motivo) || enviando}
              data-testid="confirmar-cancelamento"
            >
              {enviando ? t.cancelando : t.confirmar}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
